import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Zap, Target, Play, Eye, ShieldCheck, Activity, Award } from "lucide-react";
import { PIPELINE } from "@/lib/demo-data";
import { cn } from "@/lib/utils";
import { MonoLabel } from "./primitives";

const ICONS = [Zap, Target, Play, Eye, ShieldCheck, Activity, Award];

const STEP_ICONS: Record<string, typeof Zap> = Object.fromEntries(PIPELINE.map((s, i) => [s.id, ICONS[i]]));

/** ACTION → CLAIM → EXECUTE → OBSERVE → VERIFY → CONSEQUENCE → REPUTATION */
export function OutcomeFlow() {
  const [active, setActive] = useState(0);
  const touched = useRef(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced || touched.current) return;
    const t = setInterval(() => {
      if (touched.current) return clearInterval(t);
      setActive((a) => (a + 1) % PIPELINE.length);
    }, 3200);
    return () => clearInterval(t);
  }, [reduced]);

  const select = (i: number) => {
    touched.current = true;
    setActive(i);
  };

  const step = PIPELINE[active];

  return (
    <div data-testid="outcome-flow">
      {/* Desktop pipeline */}
      <div className="relative hidden md:block" role="tablist" aria-label="Protocol pipeline">
        <div className="absolute left-0 right-0 top-[52px] h-px bg-line" />
        <motion.div
          className="absolute left-0 top-[52px] h-px bg-gradient-to-r from-blue via-cyan to-cyan"
          animate={{ width: `${(active / (PIPELINE.length - 1)) * 100}%` }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="grid grid-cols-7">
          {PIPELINE.map((s, i) => {
            const Icon = STEP_ICONS[s.id];
            const isActive = i === active;
            const isDone = i < active;
            return (
              <button
                key={s.id}
                role="tab"
                aria-selected={isActive}
                data-testid={`pipeline-step-${s.name.toLowerCase()}`}
                onClick={() => select(i)}
                onMouseEnter={() => select(i)}
                className="group relative flex flex-col items-center gap-3 pb-2 pt-1 text-center"
              >
                <MonoLabel className={cn("text-[9px]", isActive ? "text-cyan" : "text-dim/60")}>{s.id}</MonoLabel>
                <span
                  className={cn(
                    "flex h-11 w-11 items-center justify-center border bg-base transition-all duration-300",
                    isActive
                      ? "border-cyan text-cyan shadow-[0_0_24px_rgba(0,229,255,0.35)]"
                      : isDone
                        ? "border-cyan/50 text-cyan/70"
                        : "border-line text-dim group-hover:border-electric/50 group-hover:text-ink"
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span
                  className={cn(
                    "font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200",
                    isActive ? "text-ink" : "text-dim group-hover:text-ink"
                  )}
                >
                  {s.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: vertical rail */}
      <div className="relative md:hidden">
        <div className="absolute bottom-2 left-[27px] top-2 w-px bg-line" />
        <div className="flex flex-col gap-1">
          {PIPELINE.map((s, i) => {
            const Icon = STEP_ICONS[s.id];
            const isActive = i === active;
            return (
              <button
                key={s.id}
                data-testid={`pipeline-step-mobile-${s.name.toLowerCase()}`}
                onClick={() => select(i)}
                className={cn(
                  "flex items-center gap-4 border p-3 text-left transition-colors duration-200",
                  isActive ? "border-cyan/50 bg-cyan/5" : "border-transparent"
                )}
              >
                <span
                  className={cn(
                    "relative z-10 flex h-9 w-9 shrink-0 items-center justify-center border bg-base",
                    isActive ? "border-cyan text-cyan" : "border-line text-dim"
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="flex flex-col">
                  <span className={cn("font-mono text-[10px] uppercase tracking-[0.2em]", isActive ? "text-ink" : "text-dim")}>
                    {s.id} · {s.name}
                  </span>
                  <span className="mt-0.5 text-xs text-dim">{s.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      <motion.div
        key={step.id}
        initial={reduced ? undefined : { opacity: 0, y: 10 }}
        animate={reduced ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="panel scanlines hud-corners mt-10 p-6 sm:p-8"
        data-testid="pipeline-detail-panel"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-cyan">
            {step.id} — {step.name}
          </h3>
          <MonoLabel className="text-[9px]">{step.meta}</MonoLabel>
        </div>
        <p className="mt-4 max-w-3xl font-display text-lg text-ink sm:text-xl">{step.desc}</p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-dim">{step.detail}</p>
      </motion.div>
    </div>
  );
}
